import { useCallback, useEffect, useMemo, useReducer, useState } from 'react'

import { useAuth } from '../../../Provider/AuthProvider'
import { FC, invalidateQuery, useCQuery } from '../../../Services'
import { cap } from '../../../State'
import { translations } from '../../../translations'
import { showToast } from '../../../utils/toast'
import { CALL_TYPES, DEFAULT_STATE, EMAIL_FLOW_CALL_TYPES, PRESET_STORAGE_KEY } from '../constants'

const language = cap.language.state || 'IT'
const t = translations[language] || translations.IT

const configReducer = (state, action) => {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value }
    case 'SET_NESTED':
      return {
        ...state,
        [action.field]: { ...state[action.field], [action.key]: action.value }
      }
    case 'LOAD_PRESET':
      return { ...DEFAULT_STATE, ...action.payload }
    case 'RESET':
      return { ...DEFAULT_STATE }
    default:
      return state
  }
}

/**
 * Hook centralizzato per la gestione della debug console.
 * Gestisce stato parametri, invio messaggi, preset e validazione.
 * @returns {Object} Stato e funzioni per la debug console
 */
export function useDebugChat() {
  const { user = {} } = useAuth()
  const { user: userObj = {} } = user || {}
  const { id: userId, username } = userObj

  const { data: unitsData = [] } = useCQuery(['units'])
  const { data: travellersData = [], isSuccess: isSuccessTravellers } = useCQuery(['travellers'])
  const { data: environments = [], isSuccess: isSuccessEnvironments } = useCQuery(['environments'])

  const units = useMemo(() => unitsData?.map(({ name }) => name) || [], [unitsData])

  const [config, dispatch] = useReducer(configReducer, {
    ...DEFAULT_STATE,
    userEmail: username === 'mbolla@siapcn.it' ? 'mbolla@siapcn.it' : 'mranocchia@siapcn.it',
    operatorEmail: username === 'mbolla@siapcn.it' ? 'mbolla@siapcn.it' : 'mranocchia@siapcn.it'
  })

  const [conversationId, setConversationId] = useState()
  const { data: conversation = {} } = useCQuery(['conversation', conversationId])
  const [messages, setMessages] = useState([])
  const [lastResponse, setLastResponse] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [responseStatus, setResponseStatus] = useState(null)

  const setField = useCallback((field, value) => {
    dispatch({ type: 'SET_FIELD', field, value })
  }, [])

  const setNested = useCallback((field, key, value) => {
    dispatch({ type: 'SET_NESTED', field, key, value })
  }, [])

  useEffect(() => {
    if (!isSuccessEnvironments || config.selectedEnvironment) return
    const matched = environments.find(
      ({ tomcatCode }) => user?.user?.environment?.tomcatCode === tomcatCode
    )
    if (matched) {
      dispatch({ type: 'SET_FIELD', field: 'selectedEnvironment', value: matched.codSociety || '' })
      dispatch({ type: 'SET_FIELD', field: 'codSociety', value: matched.codSociety || '' })
      dispatch({ type: 'SET_FIELD', field: 'tomcatCode', value: matched.tomcatCode || '' })
    }
  }, [environments, isSuccessEnvironments])

  const filteredEnvironments = useMemo(() => environments || [], [environments])

  const travellersOptions = useMemo(() => {
    const environmentId = environments?.find(
      ({ codSociety }) => codSociety === config.selectedEnvironment
    )?.id
    return (
      travellersData
        ?.filter(({ environmentId: envId }) => envId === environmentId)
        ?.map(({ pid }) => pid) || []
    )
  }, [travellersData, config.selectedEnvironment, environments])

  useEffect(() => {
    if (isSuccessTravellers && travellersOptions.length && !config.selectedPID) {
      dispatch({ type: 'SET_FIELD', field: 'selectedPID', value: travellersOptions[0] })
    }
  }, [travellersOptions, isSuccessTravellers])

  useEffect(() => {
    if (config.selectedEnvironment) {
      const env = environments?.find(({ codSociety }) => codSociety === config.selectedEnvironment)
      if (env) {
        dispatch({ type: 'SET_FIELD', field: 'codSociety', value: env.codSociety || '' })
        dispatch({ type: 'SET_FIELD', field: 'tomcatCode', value: env.tomcatCode || '' })
      }
    }
  }, [config.selectedEnvironment, environments])

  useEffect(() => {
    if (!conversation?.messages?.length || conversation.messages.length === messages.length) return
    const sorted = conversation.messages.sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
    )
    const hasOperatorDebug = sorted.some(({ sender }) => sender === 'debugOperator')
    setMessages(
      sorted.map(({ message, sender, createdAt }) => {
        if (sender === 'debugOperator') {
          return {
            user: 'Nora',
            message: '**[→ Operatore]**\n' + message,
            createdAt: new Date(createdAt)
          }
        }
        if (sender === 'ai' && hasOperatorDebug) {
          return {
            user: 'Nora',
            message: '**[→ Viaggiatore]**\n' + message,
            createdAt: new Date(createdAt)
          }
        }
        return {
          user: sender === 'traveller' ? conversation?.traveller?.email : 'Nora',
          message,
          createdAt: new Date(createdAt)
        }
      })
    )
  }, [conversation?.messages])

  useEffect(() => {
    const callTypeMeta = CALL_TYPES.find((ct) => ct.value === config.callType)
    if (!conversationId || callTypeMeta?.sync) return
    const interval = setInterval(() => {
      invalidateQuery(['conversation', conversationId])
    }, 5000)
    return () => clearInterval(interval)
  }, [conversationId, config.callType])

  /**
   * Assembla il payload finale da inviare all'API in base alla configurazione corrente.
   * @param {string} userInput - Il messaggio dell'utente
   * @param {Array} [attachments] - Allegati opzionali
   * @returns {Object} Payload pronto per l'invio
   */
  const buildPayload = useCallback(
    (userInput, attachments) => {
      const callTypeMeta = CALL_TYPES.find((ct) => ct.value === config.callType)

      const payload = {
        type: config.callType,
        unit: config.unit,
        user: userId,
        pid: config.selectedPID,
        tomcatCode: config.tomcatCode || user?.user?.environment?.tomcatCode,
        codSociety: config.codSociety || config.selectedEnvironment,
        userEmail: config.userEmail,
        operatorEmail: config.operatorEmail,
        userInput,
        levelFormalita: config.levelFormalita || 75,
        isDebug: config.isDebug,
        communicationChannel: config.communicationChannel || callTypeMeta?.channel || 'debug'
      }

      if (attachments?.length) payload.attachments = attachments
      if (config.pdfQuotes) payload.pdfQuotes = true
      if (config.audioMessage) payload.audioMessage = true
      // Sempre esplicito, anche quando è OFF: l'Executor distingue `false` (non inviare)
      // da assente (traffico reale, si invia). Mandandolo solo su ON, il toggle spento non
      // arrivava e una prova non in debug faceva comunque partire l'email.
      payload.sendEmail = !!config.sendEmail

      if (config.callType === 'sendMessageMidOffice') {
        payload.referencesBtmEmail = config.referencesBtmEmail
        payload.codiceCliente = config.codiceCliente
        payload.isola = config.isola
      }

      if (config.callType === 'whatsappMessage') {
        payload.referenceWhatsapp = config.referenceWhatsapp
      }

      if (
        config.callType === 'frigerioFlow' ||
        config.callType === 'gattinoniFlow' ||
        config.callType === 'goelettFlow'
      ) {
        payload.mailReferencesEmail = config.mailReferencesEmail
        payload.subject = config.subject
        payload.body = config.body || userInput
        payload.from = config.userEmail
        if (config.processLoadQuotes) payload.processLoadQuotes = config.processLoadQuotes
        if (config.errorProcessToLoad) payload.errorProcessToLoad = config.errorProcessToLoad
        if (config.selfManagedDataCollection)
          payload.selfManagedDataCollection = config.selfManagedDataCollection
        if (config.dataQuestionUnit) payload.dataQuestionUnit = config.dataQuestionUnit
        if (config.unitCallConversationExist)
          payload.unitCallConversationExist = config.unitCallConversationExist
        if (config.escludeMidOfficeFlow) payload.escludeMidOfficeFlow = config.escludeMidOfficeFlow
        if (config.goelettRoleEmails) {
          payload.goelettRoleEmails = config.goelettRoleEmails
        }
      }

      if (config.customJson) {
        try {
          const custom = JSON.parse(config.customJson)
          Object.assign(payload, custom)
        } catch (_) {
          /* custom JSON non valido, ignora */
        }
      }

      return payload
    },
    [config, userId, user]
  )

  /**
   * Valida il payload in base al tipo di chiamata selezionato.
   * @param {Object} payload - Il payload da validare
   * @returns {{ isValid: boolean, errors: string[] }}
   */
  const validatePayload = useCallback((payload) => {
    const errors = []
    if (!payload.userInput) errors.push('userInput')

    if (payload.type === 'sendMessageMidOffice') {
      if (!payload.userEmail) errors.push('userEmail')
      if (!payload.referencesBtmEmail?.idConversazione)
        errors.push('referencesBtmEmail.idConversazione')
      if (!payload.pid) errors.push('pid')
      if (!payload.codiceCliente) errors.push('codiceCliente')
      if (!payload.tomcatCode) errors.push('tomcatCode')
      if (!payload.codSociety) errors.push('codSociety')
      if (!payload.isola) errors.push('isola')
    }

    if (payload.type === 'whatsappMessage') {
      if (!payload.referenceWhatsapp?.from) errors.push('referenceWhatsapp.from')
    }

    // Flussi email per cliente: l'API li rifiuta con 500 se manca uno di questi campi
    // (es. `Missing mailReferencesEmail`), quindi vanno validati come il MidOffice.
    if (EMAIL_FLOW_CALL_TYPES.includes(payload.type)) {
      if (!payload.userEmail) errors.push('userEmail')
      if (!payload.tomcatCode) errors.push('tomcatCode')
      if (!payload.codSociety) errors.push('codSociety')
      if (!payload.mailReferencesEmail?.idConversazione)
        errors.push('mailReferencesEmail.idConversazione')
    }

    return { isValid: errors.length === 0, errors }
  }, [])

  /**
   * Campi di configurazione mancanti per la chiamata corrente, calcolati senza
   * aspettare l'invio: la console può segnalarli prima che si scriva il messaggio.
   * `userInput` è escluso perché dipende da cosa si digita.
   */
  const configIssues = useMemo(() => {
    const { errors } = validatePayload(buildPayload('probe'))
    return errors.filter((field) => field !== 'userInput')
  }, [buildPayload, validatePayload])

  /**
   * Invia un messaggio tramite l'API con la configurazione corrente.
   * @param {string} message - Testo del messaggio
   * @param {Array} [attachments] - Allegati opzionali
   * @returns {Promise<boolean>} true se l'invio è riuscito
   */
  const sendMessage = useCallback(
    async (message, attachments) => {
      const payload = buildPayload(message, attachments)
      const { isValid, errors } = validatePayload(payload)

      if (!isValid) {
        showToast(
          'warn',
          t.attenzione || 'Attenzione',
          `${t.campiObbligatori || 'Campi mancanti'}: ${errors.join(', ')}`
        )
        return false
      }

      setMessages((prev) => [...prev, { user: 'me', message }])
      setIsLoading(true)
      setResponseStatus(null)

      try {
        const startTime = Date.now()
        const res = await FC.service('public').create(payload)
        const elapsed = Date.now() - startTime

        setLastResponse({ data: res, elapsed, timestamp: new Date(), payload })
        setResponseStatus('success')

        if (
          res?.conversationData?.conversationId &&
          res.conversationData.conversationId !== conversationId
        ) {
          setConversationId(res.conversationData.conversationId)
        }

        const callTypeMeta = CALL_TYPES.find((ct) => ct.value === config.callType)
        if (callTypeMeta?.sync && res?.chatGptInfo?.messageToSend) {
          setMessages((prev) => [
            ...prev,
            {
              user: 'Nora',
              message: res.chatGptInfo.messageToSend,
              createdAt: new Date(),
              jsonPayload: res
            }
          ])
        } else if (!callTypeMeta?.sync) {
          setMessages((prev) => [
            ...prev,
            {
              user: 'Nora',
              message: `[Async] Richiesta inviata (${elapsed}ms). Tipo: ${config.callType}`,
              createdAt: new Date(),
              jsonPayload: res
            }
          ])
        }

        return true
      } catch (error) {
        setResponseStatus('error')
        setLastResponse({ error: error?.message || String(error), timestamp: new Date() })
        showToast(
          'error',
          t.errore || 'Errore',
          error?.message || "Errore durante l'invio del messaggio"
        )
        return false
      } finally {
        setIsLoading(false)
      }
    },
    [buildPayload, validatePayload, conversationId, config.callType]
  )

  const resetConversation = useCallback(() => {
    setMessages([])
    setConversationId(undefined)
    setLastResponse(null)
    setResponseStatus(null)
  }, [])

  // --- Preset management ---

  const loadPresets = useCallback(() => {
    try {
      const raw = localStorage.getItem(PRESET_STORAGE_KEY)
      return raw ? JSON.parse(raw) : {}
    } catch (_) {
      return {}
    }
  }, [])

  const savePreset = useCallback(
    (name) => {
      const presets = loadPresets()
      const { customJson, showPayloadPreview, ...configToSave } = config
      presets[name] = configToSave
      localStorage.setItem(PRESET_STORAGE_KEY, JSON.stringify(presets))
    },
    [config, loadPresets]
  )

  const deletePreset = useCallback(
    (name) => {
      const presets = loadPresets()
      delete presets[name]
      localStorage.setItem(PRESET_STORAGE_KEY, JSON.stringify(presets))
    },
    [loadPresets]
  )

  const loadPreset = useCallback(
    (name) => {
      const presets = loadPresets()
      if (presets[name]) {
        dispatch({ type: 'LOAD_PRESET', payload: presets[name] })
        resetConversation()
      }
    },
    [loadPresets, resetConversation]
  )

  const resetToDefaults = useCallback(() => {
    dispatch({ type: 'RESET' })
    resetConversation()
  }, [resetConversation])

  /**
   * Carica una configurazione client preimpostata nella debug console.
   * Popola callType, tomcatCode, codSociety, communicationChannel, userEmail
   * e genera un idConversazione univoco per il test.
   * Per Goelett, salva anche goelettRoleEmails con le email per ruolo.
   * @param {Object} clientConfig - Configurazione dal CLIENT_CONFIGS array
   */
  const loadClientConfig = useCallback(
    (clientConfig) => {
      const testConvId = `TEST${Date.now().toString(36).toUpperCase()}`
      const payload = {
        callType: clientConfig.typeEndpointCall,
        tomcatCode: clientConfig.tomcatCode,
        codSociety: clientConfig.codSociety,
        communicationChannel: clientConfig.communicationChannel || 'email',
        userEmail: clientConfig.userEmail || config.userEmail,
        operatorEmail: config.operatorEmail,
        selectedEnvironment: clientConfig.codSociety,
        isDebug: false,
        mailReferencesEmail: {
          idConversazione: testConvId,
          emailIsola: clientConfig.userEmail || config.userEmail,
          subject: `Test ${clientConfig.label} [AIC:${testConvId}]`
        },
        subject: `Test ${clientConfig.label}`,
        unit: clientConfig.typeEndpointCall
      }
      if (clientConfig.goelettRoleEmails) {
        payload.goelettRoleEmails = clientConfig.goelettRoleEmails
      }
      if (clientConfig.processLoadQuotes) payload.processLoadQuotes = clientConfig.processLoadQuotes
      if (clientConfig.errorProcessToLoad)
        payload.errorProcessToLoad = clientConfig.errorProcessToLoad
      if (clientConfig.selfManagedDataCollection)
        payload.selfManagedDataCollection = clientConfig.selfManagedDataCollection
      if (clientConfig.dataQuestionUnit) payload.dataQuestionUnit = clientConfig.dataQuestionUnit
      if (clientConfig.unitCallConversationExist)
        payload.unitCallConversationExist = clientConfig.unitCallConversationExist
      if (clientConfig.escludeMidOfficeFlow)
        payload.escludeMidOfficeFlow = clientConfig.escludeMidOfficeFlow
      dispatch({ type: 'LOAD_PRESET', payload })
    },
    [config.userEmail, config.operatorEmail]
  )

  const isReady = isSuccessTravellers && isSuccessEnvironments

  return {
    config,
    setField,
    setNested,
    units,
    travellersOptions,
    filteredEnvironments,
    environments,
    messages,
    lastResponse,
    isLoading,
    responseStatus,
    conversationId,
    isReady,
    configIssues,
    sendMessage,
    buildPayload,
    resetConversation,
    loadPresets,
    savePreset,
    deletePreset,
    loadPreset,
    resetToDefaults,
    loadClientConfig,
    userId,
    user
  }
}
